import * as React from 'react'
import { useParams, useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import type { BillingPurchaseDto } from '@lib/dto/billing.dto'
import { formatCents } from '@lib/helpers/money'
import { uuidParamSchema } from '@lib/validators/params'
import { useTenantAuth } from '@/auth/TenantAuthContext'
import { HandoffLoading, HandoffSignIn } from '@/components/billing/HandoffShell'
import { AuthShell } from '@/components/AuthShell'
import { ErrorState } from '@/components/PageState'
import { RecordFacts } from '@/components/RecordSheet'
import { Button } from '@/components/ui/button'
import { tenantApi } from '@/helpers/apiClient'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { billingKeys } from '@/hooks/queryKeys'
import { useHandoffCancel } from '@/hooks/useHandoffCancel'
import { BillingService } from '@/services/BillingService'

const service = new BillingService(tenantApi)

/** Where a purchase ended up, once it's not waiting on the person. */
function Settled({ purchase, organization }: { purchase: BillingPurchaseDto; organization: string | null }) {
  if (purchase.status === 'succeeded') {
    return (
      <AuthShell
        title="Payment received"
        description={`${formatCents(purchase.creditCents)} of credit was added${organization ? ` to ${organization}` : ''}. You can return to your AI app.`}
        size="md"
      >
        {null}
      </AuthShell>
    )
  }
  if (purchase.status === 'cancelled') {
    return <AuthShell title="Payment cancelled" description="Nothing was charged. You can return to your AI app." size="md">{null}</AuthShell>
  }
  if (purchase.status === 'failed') {
    return (
      <AuthShell
        title="The payment didn’t go through"
        description={`${purchase.failureMessage ? `${purchase.failureMessage} ` : ''}Nothing was charged. Ask your AI app to start again if you still want credit.`}
        size="md"
      >
        {null}
      </AuthShell>
    )
  }
  return (
    <AuthShell title="This payment link has expired" description="Nothing was charged. Ask your AI app to start again if you still want credit." size="md">
      {null}
    </AuthShell>
  )
}

/**
 * PixelSign's checkout page for credit an AI app asked to buy. It shows the
 * amount and hands the person to Stripe; opening it never pays. Stripe sends
 * them back here (`?paid=1`, or `?cancelled=1` from its back link), and the
 * AI app, which is waiting, sees the result.
 */
export function BillingPayPage() {
  const { tenantId: rawTenantId, purchaseId: rawPurchaseId } = useParams()
  const parsedTenantId = uuidParamSchema.safeParse(rawTenantId)
  const parsedPurchaseId = uuidParamSchema.safeParse(rawPurchaseId)
  const tenantId = parsedTenantId.success ? parsedTenantId.data : null
  const purchaseId = parsedPurchaseId.success ? parsedPurchaseId.data : null
  const [params] = useSearchParams()
  const paid = params.get('paid') === '1'
  const cancelled = params.get('cancelled') === '1'
  const tenant = useTenantAuth()
  const organization = tenant.session?.memberships.find((membership) => membership.tenantId === tenantId)?.tenantName ?? null
  const query = useQuery({
    queryKey: billingKeys.purchase(tenantId, 'tenant', purchaseId),
    queryFn: () => {
      if (tenantId === null || purchaseId === null) throw new Error('Payment link is not valid')
      return service.purchase(tenantId, purchaseId)
    },
    enabled: tenant.isAuthenticated && !cancelled && tenantId !== null && purchaseId !== null,
    refetchInterval: (current) => {
      const purchase = current.state.data
      if (!paid || purchase?.status !== 'pending' || purchase.failureMessage !== null) return false
      return 2_000
    },
    retry: false,
  })
  const cancel = useHandoffCancel(tenantId, purchaseId, {
    returnedCancelled: cancelled,
    signedIn: tenant.isAuthenticated,
    send: (target) => service.cancelPurchase(target.tenantId, target.id),
  })
  const [leaving, setLeaving] = React.useState(false)

  if (tenantId === null || purchaseId === null) {
    return (
      <AuthShell title="Buy credit" size="md">
        <ErrorState message="That payment link is not valid." />
      </AuthShell>
    )
  }

  if (tenant.isLoading) return <HandoffLoading />
  if (!tenant.isAuthenticated) return <HandoffSignIn title="Buy credit" />
  // A cancel from this page's button shows its outcome too, not only Stripe's back link.
  const purchase = cancel.result ?? (cancelled ? undefined : query.data)
  const failure = cancelled ? cancel.error : query.error
  if (failure) {
    return (
      <AuthShell title="Buy credit" size="md">
        <ErrorState message={apiErrorMessage(failure, 'Could not load this payment.')} />
      </AuthShell>
    )
  }
  if (!purchase) return <HandoffLoading />
  if (purchase.status !== 'pending') return <Settled purchase={purchase} organization={organization} />
  if (purchase.failureMessage) {
    return <AuthShell title="This payment needs review" description={purchase.failureMessage} size="md">{null}</AuthShell>
  }
  if (paid) return <AuthShell title="Confirming your payment…" description="This takes a few seconds." size="md">{null}</AuthShell>

  const checkoutUrl = purchase.checkoutUrl
  return (
    <AuthShell title={organization ? `Buy credit for ${organization}` : 'Buy credit'} size="md">
      <div className="space-y-4">
        <RecordFacts>
          <dt className="text-muted-foreground">Credit</dt>
          <dd className="tabular-nums">{formatCents(purchase.creditCents)}</dd>
        </RecordFacts>
        {cancel.error ? <ErrorState message={apiErrorMessage(cancel.error, 'Could not cancel. Try again.')} /> : null}
        <p className="text-caption text-muted-foreground">
          You’ll pay by card on Stripe’s page, where sales tax is added if it applies. Your AI app asked for this; if you didn’t, cancel.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" disabled={cancel.isPending || leaving} onClick={cancel.cancel}>
            Cancel
          </Button>
          <Button
            disabled={!checkoutUrl || leaving}
            onClick={() => {
              if (!checkoutUrl) return
              setLeaving(true)
              window.location.assign(checkoutUrl)
            }}
          >
            Pay with card
          </Button>
        </div>
      </div>
    </AuthShell>
  )
}
